import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import TextAreaInput from '../../components/TextAreaInput';
import SelectInput from '../../components/SelectInput';
import CheckboxInput from '../../components/CheckboxInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import headerFooterApi from '../../api/headerFooterApi';
import { useToast } from '../../contexts/ToastContext';
import { ROUTES } from '../../constants';

const EditHeaderFooter = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    is_active: 0,
    position: 'header',
    slug: '', // Added slug to formData
  });
  const [codeFound, setCodeFound] = useState(true);

  useEffect(() => {
    const fetchCode = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const response = await headerFooterApi.getHeaderFooterByIdentifier(id);
        if (response.success && response.data) {
          const codeToEdit = response.data;
          setFormData({
            name: codeToEdit.name,
            code: codeToEdit.code,
            is_active: codeToEdit.is_active ? 1 : 0,
            position: codeToEdit.position,
            slug: codeToEdit.slug, // Set slug from fetched data
          });
          setCodeFound(true);
        } else {
          setCodeFound(false);
          setGeneralError(response.message || 'Code not found.');
        }
      } catch (err) {
        console.error('Failed to fetch code:', err);
        setCodeFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load code. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchCode();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? (checked ? 1 : 0) : value,
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const data = new FormData();
    for (const key in formData) {
      data.append(key, formData[key]);
    }

    try {
      await headerFooterApi.updateHeaderFooter(id, data);
      addToast('Header/Footer code updated successfully!', 'success');
      navigate(ROUTES.HEADER_FOOTER);
    } catch (err) {
      console.error('Failed to update code:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update code. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const positionOptions = [
    { value: 'header', label: 'Header' },
    { value: 'footer', label: 'Footer' },
  ];

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!codeFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">Code Not Found</h1>
        <p className="text-text-gray">{generalError || 'The header/footer code you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate(ROUTES.HEADER_FOOTER)}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit Header/Footer Code</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Name"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          required={true}
          placeholder="Name"
          error={validationErrors?.name?.[0]}
        />
        <TextAreaInput
          label="Code"
          name="code"
          id="code"
          value={formData.code}
          onChange={handleChange}
          required={true}
          rows={6}
          className="font-mono"
          placeholder="Code"
          error={validationErrors?.code?.[0]}
        />
        <SelectInput
          label="Position"
          name="position"
          id="position"
          value={formData.position}
          onChange={handleChange}
          options={positionOptions}
          required={true}
          error={validationErrors?.position?.[0]}
        />
        <CheckboxInput
            label="Active"
            name="is_active"
            id="is_active"
            checked={formData.is_active}
            onChange={handleChange}
        />
        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update Code
          </Button>
          <Button
            type="button"
            onClick={() => navigate(ROUTES.HEADER_FOOTER)}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditHeaderFooter;